/**
 * "Fill out your first daily report" — the reference tour, and the one the anti-rot guard is proven
 * against.
 *
 * SCOPE. Written for a FOREMAN, the role that walks this workflow end to end: tk:create and
 * tk:submit are both in the foreman seed (identity backend database/session.py:318-343) and the
 * whole path lives inside the timekeeping module they already have in their nav. It stops before
 * Manager Approve on purpose — a foreman has tk:review but not tk:approve, so that button is never
 * rendered for them (ReportReview.jsx:109-115), and a step pointing at it would be a step about a
 * button they will never see.
 *
 * It is also NOT written for field hands. They have no logins — they scan a QR sticker and type a
 * badge number and a PIN — so a click-through tour can never reach them.
 *
 * VOICE. The reader is a new hire on their first week, not an engineer. Every step says what the
 * thing is FOR and what happens when they touch it. No entity names, no status enums, no
 * permission strings in the copy.
 *
 * Steps 8-10 fire the app's REAL Save / Verify / Submit handlers when clicked, because that is the
 * premise of the feature: the new hire is filling in a real report, not watching a slideshow.
 *
 * ── AND THEY MUST NOT LEAVE A REPORT BEHIND ─────────────────────────────────────────────────────
 * A submitted daily report is not a row. It mints field tickets, time entries and posted cost
 * against a live job, notifies an approver, and cannot be un-minted by deleting anything afterwards.
 * So this tour runs as a PRACTICE RUN — the default, see `practice` in tourSchema.js — and every
 * write on it is answered inside the browser and never reaches the server. Two of those answers are
 * spelled out in shared/tour/practiceResponses.js rather than left to the generic one, because
 * Verify and Submit return a VERDICT the client cannot invent: answered generically, the last step
 * of this tour showed a foreman "Cannot submit — fix errors below" at the exact moment it was
 * supposed to be teaching them what a successful submit looks like.
 *
 * Nothing in this file may opt out of that (`practice: false`), and the anchor guard fails loudly if
 * anything tries.
 *
 * ── EVERY CLAIM BELOW WAS CHECKED AGAINST THE COMPONENT, NOT AGAINST THE SURVEY ─────────────────
 * Three things the first draft of this tour got wrong, all found by reading the code:
 *
 *  1. The date does NOT lock "once you submit". It locks whenever the page is opened with a report
 *     id in the URL and the report is not rejected — `disabled={!isEditable || (isEdit && status
 *     !== 'rejected')}` (DailyReportEntry.jsx:895). So it is editable while you are still on the
 *     blank /report/new screen, and locked the next time you open that same report. The copy now
 *     describes that, because the old copy would have had a foreman hunting for an unlocked date
 *     field that had already locked.
 *
 *  2. Hours with no job attached are SILENTLY DISCARDED. buildPayload filters
 *     `finalBlocks.filter(tb => tb.employee_id && tb.project_id)` (:714), in all three entry modes.
 *     Nothing warns; the line just does not reach the server. That is the single most expensive
 *     thing a new foreman can get wrong on this screen, so it is its own step (assign-the-job).
 *
 *  3. Equipment / Materials / Work Descriptions can only be attached to a job that ALREADY has
 *     hours on it — their project dropdowns are filtered to `projects.filter(p =>
 *     timeBlocks.some(tb => tb.project_id === p.id))` (:1280, :1449). A foreman who opens Work
 *     Descriptions first finds an empty dropdown and no explanation, so the step says to do hours
 *     first.
 */
import { defineTour, AUDIENCE, ADVANCE_ON } from '../tourSchema'

export default defineTour({
  id: 'first-daily-report',
  title: 'Fill out your first daily report',
  summary: 'From the nav tab to hitting Submit — the one you will do every day.',
  audience: AUDIENCE.ONBOARDING,
  module: 'timekeeping',
  // GATED AS A WHOLE, not step by step, and the two are not equivalent here. The first two steps are
  // navigation — a nav tab and a New Report button, both rendered for anyone with the module — so
  // they carry no permission and nothing can filter them out. Every step after them needs tk:create.
  // Without this line, a user who has the timekeeping module but not tk:create (a per-user Module
  // Access grant, or a per-user deny on tk:create — both are checkboxes in routes/Admin.jsx) is
  // offered a "2 steps" tour that walks them to /timekeeping/report/new, marks itself Finished, and
  // leaves them on a screen whose Save Draft returns 403 (daily_reports.py:183-187).
  permission: 'tk:create',

  steps: [
    {
      id: 'open-daily-reports',
      anchor: 'timekeeping.nav.daily-reports',
      route: '/timekeeping',
      module: 'timekeeping',
      title: 'Start here',
      body: 'A daily report is the record of who worked today, which job they worked on, and for how long. It is what the office pays and bills from. Every one you have written lives under this tab — open it.',
      // Waits for the LIST SCREEN, not for the click that was meant to reach it. Both navigation
      // steps of this tour do, for the same reason: every step after them lives on a screen you only
      // get to by doing the one before, so a click that did not land used to cascade — one missed
      // press and the remaining eight steps all pointed at controls on pages the reader was not on.
      advanceOn: ADVANCE_ON.DO,
      until: { appears: 'daily-report.new' },
    },
    {
      id: 'start-a-report',
      anchor: 'daily-report.new',
      route: '/timekeeping/reports',
      module: 'timekeeping',
      title: 'Open a blank one',
      body: 'One report covers your crew for one day. Start a fresh one each morning instead of reopening yesterday’s.',
      advanceOn: ADVANCE_ON.DO,
      until: { appears: 'daily-report.date' },
    },
    {
      id: 'set-the-date',
      anchor: 'daily-report.date',
      route: '/timekeeping/report/new',
      module: 'timekeeping',
      permission: 'tk:create',
      title: 'Check the date first',
      body: 'This fills in with today. If you are writing up a day that has already passed, change it now — once this report is saved and you come back to it later, the date is fixed unless the office sends it back to you.',
      advanceOn: ADVANCE_ON.NEXT,
    },
    {
      id: 'crew-and-hours',
      anchor: 'daily-report.crew-hours',
      route: '/timekeeping/report/new',
      module: 'timekeeping',
      permission: 'tk:create',
      title: 'Your crew is already filled in',
      body: 'This is the part that matters. Everyone on your crew is listed for you and marked present, with a normal 7-to-5 day on each of them. You are correcting that, not typing it from scratch.',
      advanceOn: ADVANCE_ON.NEXT,
    },
    {
      id: 'entry-mode',
      anchor: 'daily-report.entry-mode',
      route: '/timekeeping/report/new',
      module: 'timekeeping',
      permission: 'tk:create',
      title: 'Three ways to type the same thing',
      body: 'Detailed asks for a start and end time. Split Hours just asks how many hours went to each job. Grid is a spreadsheet. Switching keeps what you have already entered, and the app remembers the one you picked for next time — so choose whichever matches the notes in your pocket.',
      advanceOn: ADVANCE_ON.NEXT,
    },
    {
      id: 'assign-the-job',
      anchor: 'daily-report.hours-entry',
      route: '/timekeeping/report/new',
      module: 'timekeeping',
      permission: 'tk:create',
      title: 'Put every hour on a job',
      body: 'Each person gets a line: the job, their times, and their lunch. Pick the job before anything else — a line with no job on it is thrown away when the report saves, so those hours never reach the office and nobody is told. If someone worked two jobs, give them a second line rather than splitting the difference.',
      advanceOn: ADVANCE_ON.NEXT,
    },
    {
      id: 'work-descriptions',
      anchor: 'daily-report.work-descriptions',
      route: '/timekeeping/report/new',
      module: 'timekeeping',
      permission: 'tk:create',
      title: 'Say what you actually did',
      body: 'Open this to write down the work performed, and use Equipment and Materials above it the same way. One catch worth knowing: these only offer you jobs that already have hours on them, so finish the crew’s hours first or the list here comes up empty.',
      advanceOn: ADVANCE_ON.CLICK,
    },
    {
      id: 'save-a-draft',
      anchor: 'daily-report.save-draft',
      route: '/timekeeping/report/new',
      module: 'timekeeping',
      permission: 'tk:create',
      title: 'Save as you go',
      body: 'Saving keeps your work without sending it anywhere — nobody reviews a draft. Hit this whenever you get pulled away, and finish the report later.',
      // Waits for the SAVE ITSELF to go out. The button can be pressed and produce nothing — the
      // screen builds its payload first and stops on an error — and a tour that walked on from that
      // would teach a foreman that his day was stored when it was not. That is the single most
      // expensive false claim available on this screen.
      //
      // ONE KNOWN FALSE NEGATIVE, AND IT IS THE SAFE DIRECTION. The screen POSTs to create and PUTs
      // to update, and a gate waits for exactly one signal. This waits for the CREATE, which is what
      // pressing Save Draft does on the blank report step 2 opened — the tour's own path. A reader
      // who wandered off and saved a draft before being told to has an id already, so their press
      // sends a PUT and this gate does not see it; they sit here until the stuck panel offers the
      // skip, which is why the hint below names that case. The alternative — advancing on the click —
      // fails in the direction that matters more: it would tell somebody their day was stored when
      // the save had been refused.
      advanceOn: ADVANCE_ON.DO,
      until: { requested: 'POST /daily-reports' },
      stuckHint: 'Two reasons this can sit here. There may be a message in red at the top — the save '
        + 'is being refused, not ignored. Or you saved this report earlier already, in which case '
        + 'you have done this step: skip it.',
    },
    {
      id: 'check-it',
      anchor: 'daily-report.verify',
      route: '/timekeeping/report/new',
      module: 'timekeeping',
      permission: 'tk:create',
      title: 'Check it before you send it',
      body: 'Verify runs the same checks the office runs and lists anything missing while you can still fix it. If the job requires a safety form, it says so here and gives you a link straight to it. Press it and read what comes back.',
      // A CLICK, on purpose, where the two steps either side of it wait for a request. Pressing
      // Verify always produces a visible answer — either the list of what is wrong, or a pass — and
      // reading that answer IS the step. Waiting for the request would strand a reader whose report
      // has something wrong with it on the one step whose entire job is to show them what is wrong.
      advanceOn: ADVANCE_ON.CLICK,
    },
    {
      id: 'send-it',
      anchor: 'daily-report.submit',
      route: '/timekeeping/report/new',
      module: 'timekeeping',
      permission: 'tk:submit',
      title: 'Send it to the office',
      body: 'Submit saves the report and hands it over for review. It runs those checks one more time first — if something is missing the report stays with you and tells you what to fix. When it goes through you land back on the timekeeping dashboard, and if the office finds a problem later they send it back for you to correct and resend.',
      // THE LAST STEP OF THE TOUR, so what it claims is what the reader walks away believing. It
      // waits for the submit request, because pressing Submit on a report with something wrong with
      // it sends NOTHING — the screen refuses and lists why. On a click gate that refusal still ended
      // the tour, and the launcher then showed "Fill out your first daily report — Finished" to a
      // foreman whose report was never sent. The tour now stays put and the screen's own red list is
      // the instruction.
      advanceOn: ADVANCE_ON.DO,
      until: { requested: 'POST /submit' },
      stuckHint: 'If a list of problems came up instead, that is the report being refused — fix those lines and press Submit again.',
    },
    /**
     * A CLOSING STEP, AND IT IS NOT DECORATION — the same one crew-time and equipment-sticker got,
     * arriving late to the tour it matters most on.
     *
     * `send-it` above is gated on the REQUEST, so it is satisfied the instant the submit is emitted,
     * and while it was the last step the tour ENDED there — which switches practice mode off with
     * the spotlight still on Submit, one press away from a real one. That press is worse here than
     * anywhere else in the five tours: a submitted daily report mints field tickets, time entries and
     * posted cost against a live job and notifies an approver, and none of it can be un-minted by
     * deleting anything afterwards. The rule was already written down in
     * __tests__/tourWalkthrough.test.jsx and simply had not been applied to this tour.
     *
     * The `route` is doing a second job. It takes the reader off the report screen entirely, which is
     * where the app was going to send them anyway (handleSubmit navigates here two seconds after a
     * real submit) — so the practice report goes with the screen instead of sitting behind the card.
     */
    {
      id: 'thats-the-day',
      anchor: 'timekeeping.nav.my-hours',
      route: '/timekeeping',
      module: 'timekeeping',
      title: 'That is the day sent',
      body: 'The office picks it up from here, and if they find a problem it comes back under Daily Reports for you to fix and resend — you do not start again. The hours you wrote down become everybody’s time, including your own, and this tab is where you see yours. Write one of these every day you work: a day nobody writes up is a day the office cannot pay anyone for or bill anybody for, and by the time that is noticed nobody remembers who was where.',
      advanceOn: ADVANCE_ON.NEXT,
    },
  ],
})
